import { Modal } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import { t } from 'ttag';

// useNativeSafeExit shows a browser native confirmation dialog when the `isSafe` condition
// is not met and the user attempts to close or refresh the browser
const useNativeSafeExit = (isSafe, global = window) => {
  useEffect(() => {
    function beforeUnloadlistener(event) {
      if (!isSafe) {
        event.preventDefault();

        // if you're wondering why do we have to do that then take your fight
        // to w3c or whoever designed this stupid API. We _have_ to set the return
        // value to be any string to show the default native confirmation popup.

        // eslint-disable-next-line no-param-reassign
        event.returnValue = '¯\\_(ツ)_/¯';
      }
    }

    global.addEventListener('beforeunload', beforeUnloadlistener);

    return () => {
      global.removeEventListener('beforeunload', beforeUnloadlistener);
    };
  }, [isSafe, global]);
};

// SafeExit blocks in-app navigation while there are unsaved changes. v6's
// useBlocker requires a data router (createBrowserRouter + RouterProvider),
// which App.jsx provides. useNativeSafeExit covers browser unload (close or
// refresh) separately, since useBlocker only intercepts router navigation.
const SafeExit = ({ isSafe, title, content }) => {
  useNativeSafeExit(isSafe);
  const blocker = useBlocker(!isSafe);

  return (
    <Modal
      open={blocker.state === 'blocked'}
      destroyOnHidden
      title={title ?? t`Continue without saving?`}
      okText={t`Yes, Continue`}
      cancelText={t`No, Cancel`}
      okButtonProps={{ type: 'primary', danger: true }}
      onOk={() => blocker.proceed()}
      onCancel={() => blocker.reset()}
    >
      {content ?? t`There are unsaved changes on this page. Do you still want to leave?`}
    </Modal>
  );
};

SafeExit.propTypes = {
  isSafe: PropTypes.bool.isRequired,
  title: PropTypes.string,
  content: PropTypes.string,
};

export default SafeExit;
